import { ApiError } from "./client.js";
import { TAKEOFF_DB } from "../lib/takeoffDb.js";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

function readCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

// Multipart upload — deliberately NOT reusing postJson from businessData.js:
// the body is a FormData (browser sets the multipart boundary itself), so we
// must not set Content-Type manually and must not JSON.stringify the body.
async function uploadDrawing(projectId, file) {
  const csrfToken = readCookie("business_csrf");
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_URL}/api/business/drawings?projectId=${encodeURIComponent(projectId)}`, {
    method: "POST",
    headers: {
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    credentials: "include",
    body: formData,
  });
  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await res.json().catch(() => null) : null;
  if (!res.ok) throw new ApiError(res.status, data);
  return data; // { id, filename, mimeType, fileSizeBytes, createdAt }
}

async function downloadDrawing(serverId) {
  const res = await fetch(`${API_URL}/api/business/drawings/${encodeURIComponent(serverId)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new ApiError(res.status, null);
  return res.arrayBuffer();
}

async function deleteDrawing(serverId) {
  const csrfToken = readCookie("business_csrf");
  const res = await fetch(`${API_URL}/api/business/drawings/${encodeURIComponent(serverId)}`, {
    method: "DELETE",
    headers: {
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    credentials: "include",
  });
  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await res.json().catch(() => null) : null;
  if (!res.ok) throw new ApiError(res.status, data);
  return data; // { ok: true }
}

export const drawingsApi = {
  upload: uploadDrawing,
  download: downloadDrawing,
  remove: deleteDrawing,
};

// Reusable helper for the read/render hot path: try the server only as a
// fallback when a drawing's bytes aren't in local IndexedDB (e.g. opening a
// project on a different browser), then warm the local cache so subsequent
// reads hit TAKEOFF_DB directly. Returns the ArrayBuffer, or null if there's
// no serverId to fall back to or the fetch fails.
export async function fetchAndCacheDrawing(localId, serverId) {
  if (!serverId) return null;
  try {
    const buf = await downloadDrawing(serverId);
    await TAKEOFF_DB.save(localId, buf);
    return buf;
  } catch (e) {
    console.warn("Failed to fetch drawing from server", e);
    return null;
  }
}
